// Activity — the Office audit log. docs/REBUILD_PARITY.md row "Activity log
// with user filter", the one capability the classic ActivityLog has that the
// rebuild did not: who did it, filterable by person.
//
// ── THE CONTRACT, read off server/routes/activity.js (2026-09-03) ───────────
// Every name below is in that file. Nothing here is inferred from a field name.
//
//   GET /api/activity
//     GATE  an inline ROLE check — `req.user.role !== 'admin' && !== 'manager'`
//           → 403 { error: 'Access denied' }. NOT a permission check, and the
//           difference bites: server/database.js seeds `accounting` with
//           activity_log: ['read'] and this handler refuses them anyway.
//           ACTIVITY_ROLES below is that list; OfficeActivity.test.js reads all
//           three handlers and fails if either the list or the shape moves.
//     ?entity_type ?user_id ?action_type ?start_date ?end_date ?limit ?offset
//           All optional. `limit` defaults to 100, `offset` to 0; both go
//           through parseInt and are echoed back in the body. start_date and
//           end_date are YYYY-MM-DD in the COMPANY timezone — the handler
//           converts through buildLocalDateRangeFilter, and either one alone
//           is a valid open-ended range.
//     ->  { activity: [ { id, user_id, entity_type, entity_id, entity_name,
//                         action_type, description, details, created_at,
//                         user_name, user_role } ],
//           total, limit, offset }
//         `total` is COUNT(*) of the same WHERE clause before the limit, so it
//         is the size of the filtered set, not of the table. `details` is
//         JSON.parse'd by the handler when it parses and left a string when it
//         does not — this screen does not render it.
//
//   GET /api/activity/stats?days=N
//     GATE  the same inline role check. `days` defaults to 7.
//     ->  { total,
//           byEntityType: [{ entity_type, count }],   every type, desc by count
//           byActionType: [{ action_type, count }],   every action, desc
//           byUser:       [{ user_id, user_name, count }],  LIMIT 10
//           byDay:        [{ date, count }],          company-local dates
//           period:       'Last N days' }
//         Counts are UNFILTERED: they answer "what does the log contain over
//         this window", never "what do the filters below select". byUser and
//         byDay are not rendered here — the panels that would use them are
//         Reports' territory.
//
//   GET /api/activity/recent?limit=N
//     GATE  the same inline role check.
//     ->  a BARE ARRAY of the same row shape. It takes no filter parameter at
//         all, so it cannot serve a user filter. insight/Reports.js already
//         renders it as its "Recent activity" panel and keeps it; this screen
//         deliberately does not duplicate that panel.
//
//   GET /api/admin/users
//     GATE  checkRole('admin', 'manager', 'accounting') — WIDER than the three
//           activity handlers, so on this screen the roster normally answers.
//     ->  [{ id, name, role, is_active, … }]
//         The roster the person filter needs, and a SECOND request that can be
//         refused on its own. A refused roster renders as a picker with one
//         option, which reads as "nobody else has an account" — a claim about
//         the company drawn from a request that never answered. pickerNote()
//         exists for exactly that and is used below.
//
// ── WHY THE TYPE AND ACTION LISTS ARE NOT HARDCODED ────────────────────────
// There is no enum on the server. Every caller of logGlobalActivity passes its
// own strings, and server/routes/jobs.js passes a COMPUTED one
// ('status_changed', and whatever a future activity descriptor is called). The
// classic screen hardcodes seven entity types and three of them — timesheet,
// media, user — are written by no handler in server/, so a manager can filter
// to "Timesheets" and be told, every time, that nothing happened.
//
// So both option lists come from /activity/stats, which is the DISTINCT set the
// log actually holds over its window. The window is stated on the control,
// because "types seen in the last 90 days" is a different claim from "types
// that exist" and the reader has to be able to tell.

import React, { useMemo, useState } from 'react';
import { Stat, Pill, Notice, Skeleton, EmptyState, FwButton, FwField, FwInput, FwSelect } from '../../../ui/fw';
import { serverSentence } from '../../shared/http';
import { useCan } from '../../../hooks/usePermission';
import {
  useOfficeQuery, PageHeader, Toolbar, Panel, pickerNote, fmtDateTime, ROLE_TONE,
  MoreFilters, countActive,
} from '../records/OfficePage';

/**
 * The roles the three /api/activity handlers admit, duplicated from
 * server/routes/activity.js on purpose and checked against it by a test, per
 * CLAUDE.md "declare a contract once, and check it against the server".
 *
 * This WAS a role list, and deliberately so: a permission gate here would have
 * been a second opinion that can disagree with the server, which is the
 * mistake this file's contract exists to prevent. The rule was "ask what the
 * handlers ask", and the handlers asked the role.
 *
 * On 2026-09-09 the handlers changed. routes/activity.js now asks
 * checkPermission('activity_log', 'read'), so the SAME rule now points the
 * other way and the screen asks the capability. The reasoning is unchanged;
 * only the server's question moved.
 *
 * Why it moved: a role comparison cannot see users.extra_permissions, so an
 * admin ticking "Activity log: read" for an office assistant was obeyed
 * nowhere. Now the server obeys it and the screen shows it — and
 * navGateCoverage.test.js caught the half-migration where the server obeyed a
 * grant the menu still hid, which is why apps/shared/navigation.js carries
 * `needs: ['activity_log', 'read']` on this row.
 */
// The seeded roles that hold `activity_log.read` in server/database.js. Kept
// as documentation of who reaches this today — it is NOT the gate any more.
//
// Until 2026-09-09 the server asked `req.user.role` here and this list was the
// gate on both sides. The permissions audit made routes/activity.js ask
// checkPermission('activity_log','read'), so the client asks the same thing:
// an admin who ticks "Activity log: read" for somebody is now obeyed by the
// server, and a role list could never see that grant.
export const ACTIVITY_ROLES = ['admin', 'manager', 'accounting'];

/** The stats window, and therefore how far back the type pickers can see. */
export const STATS_DAYS = 90;

/** Rows per page. The handler's own default is 100; 50 is the classic screen's. */
export const PAGE_SIZE = 50;

/** Every query parameter GET /api/activity reads. Pinned against the handler. */
export const LIST_PARAMS = ['entity_type', 'user_id', 'action_type', 'start_date', 'end_date', 'limit', 'offset'];

const EMPTY_FILTERS = { entity_type: '', user_id: '', action_type: '', start_date: '', end_date: '' };
const DASH = '—';

/** 'time_entry' → 'Time entry', 'status_changed' → 'Status changed'. */
export function labelOf(token) {
  const s = String(token === null || token === undefined ? '' : token).replace(/[_-]+/g, ' ').trim();
  if (!s) return DASH;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** A count the server sent. `DASH` when there is no number — never an invented 0. */
export function countText(n) {
  if (n === null || n === undefined || n === '') return DASH;
  const v = Number(n);
  return Number.isFinite(v) ? String(v) : DASH;
}

/** Is any filter set? Decides whether an empty result is "no match" or "no log". */
export function anyFilter(filters) {
  return LIST_PARAMS.some((k) => k !== 'limit' && k !== 'offset' && !!(filters && filters[k]));
}

/** The path for GET /api/activity from the filter state. Pure. */
export function activityPath(filters, offset, limit = PAGE_SIZE) {
  const f = filters || {};
  const qs = new URLSearchParams();
  ['entity_type', 'user_id', 'action_type', 'start_date', 'end_date'].forEach((k) => {
    if (f[k]) qs.set(k, String(f[k]));
  });
  qs.set('limit', String(limit));
  qs.set('offset', String(Math.max(0, Math.trunc(Number(offset) || 0))));
  return `/activity?${qs.toString()}`;
}

/**
 * The "showing" line, computed from the RESPONSE BODY and never from component
 * state. Returns null when the numbers are not all there, or when there are no
 * rows to describe — the empty state says that instead.
 *
 * This is the classic screen's #180 defect in its second form. ActivityLog.js
 * kept the previous request's rows when a page turn failed while `offset` had
 * already moved on, so records 1–50 rendered under "Showing 51 – 100 of 250":
 * the page appearing to turn and the same events appearing to come back, on a
 * screen whose entire job is answering "did this happen". A range read off the
 * same body as the rows cannot say that.
 */
export function pageRange(body, count) {
  if (!body) return null;
  const offset = Number(body.offset);
  const total = Number(body.total);
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  if (!Number.isFinite(offset) || !Number.isFinite(total) || n <= 0) return null;
  return `${offset + 1} – ${offset + n} of ${total}`;
}

/** Is there a page after this one? Off the response body, for the same reason. */
export function hasNextPage(body, count) {
  if (!body) return false;
  const offset = Number(body.offset);
  const total = Number(body.total);
  const n = Math.max(0, Math.trunc(Number(count) || 0));
  if (!Number.isFinite(offset) || !Number.isFinite(total) || n <= 0) return false;
  return offset + n < total;
}

/** The parts of one event's meta line that the row actually carries. */
export function metaBits(row) {
  const bits = [];
  if (!row) return bits;
  if (row.action_type) bits.push(labelOf(row.action_type));
  if (row.entity_type) bits.push(labelOf(row.entity_type));
  if (row.entity_name) bits.push(String(row.entity_name));
  return bits;
}

export default function Activity() {
  // The capability, not the role list — this is the same question
  // routes/activity.js now asks (checkPermission('activity_log','read')), and
  // useCan carries the same admin shortcut the server has. `useRole` is gone
  // from this screen with it: nothing here branches on a role any more.
  const mayRead = useCan('activity_log', 'read');

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);

  // Both dates set the wrong way round selects nothing. Saying so beats
  // sending it and rendering the empty answer as "nothing happened".
  const rangeOk = !(filters.start_date && filters.end_date && filters.start_date > filters.end_date);

  // The key carries the whole path, so changing a filter starts a NEW query
  // rather than overwriting the running one's rows. That is what makes a slow
  // first request unable to land under a second filter — the last-arrival-wins
  // race the classic screen hand-rolls a sequence number for.
  const listPath = activityPath(filters, offset);
  const listQ = useOfficeQuery(['office', 'activity', listPath], listPath, { enabled: mayRead && rangeOk });
  const statsQ = useOfficeQuery(['office', 'activity', 'stats', STATS_DAYS], `/activity/stats?days=${STATS_DAYS}`, { enabled: mayRead });
  // Same key as insight/Reports.js, so the two screens share one roster fetch.
  const usersQ = useOfficeQuery(['admin', 'users'], '/admin/users', { enabled: mayRead });

  const body = listQ.data && typeof listQ.data === 'object' && !Array.isArray(listQ.data) ? listQ.data : null;
  // NOT `q.data || []` and NOT `body.activity || []`. An empty array is an
  // answer ("nothing matched") and a refusal is not; collapsing the two is the
  // defect family docs/AGENT_LEARNINGS.md 2026-08-17 records, and on an audit
  // log it answers "did this happen" with a confident no.
  const rows = body && Array.isArray(body.activity) ? body.activity : null;
  const stats = statsQ.data && typeof statsQ.data === 'object' ? statsQ.data : null;
  const entityTypes = stats && Array.isArray(stats.byEntityType) ? stats.byEntityType : null;
  const actionTypes = stats && Array.isArray(stats.byActionType) ? stats.byActionType : null;
  const users = Array.isArray(usersQ.data) ? usersQ.data : null;

  const periodText = stats && typeof stats.period === 'string' && stats.period
    ? stats.period.toLowerCase()
    : `last ${STATS_DAYS} days`;

  const filtered = anyFilter(filters);
  const range = pageRange(body, rows ? rows.length : 0);
  const nextPage = hasNextPage(body, rows ? rows.length : 0);
  const prevPage = !!body && Number(body.offset) > 0;

  const setFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setOffset(0);
  };
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setOffset(0);
  };

  const statTiles = useMemo(() => {
    if (!stats) return [];
    const tiles = [{ key: '__total', label: 'Events', value: countText(stats.total) }];
    (entityTypes || []).slice(0, 3).forEach((t) => {
      tiles.push({ key: t.entity_type, label: labelOf(t.entity_type), value: countText(t.count) });
    });
    return tiles;
  }, [stats, entityTypes]);

  if (!mayRead) {
    return (
      <div className="or-page" data-screen="office-Activity">
        <PageHeader title="Activity log" />
        <EmptyState icon="lock" title="Not available to this role" data-refused="activity">
          Only office staff can read the activity log. Ask an admin if you need it.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="or-page" data-screen="office-Activity">
      <PageHeader title="Activity log" meta="Who changed what, and when.">
        <FwButton variant="ghost" icon="refresh" onClick={() => listQ.refetch()} loading={listQ.isFetching}>
          Refresh
        </FwButton>
      </PageHeader>

      {statsQ.isPending ? <Skeleton lines={2} /> : null}
      {statsQ.error ? (
        <Notice tone="warn" data-panel-error="activity-stats">
          {serverSentence(statsQ.error, 'Could not load the activity summary, so the counts and the type filters below are missing.')}
        </Notice>
      ) : null}
      {statTiles.length > 0 ? (
        <div className="or-stats">
          {statTiles.map((t) => <Stat key={t.key} label={t.label} value={t.value} hint={periodText} />)}
        </div>
      ) : null}

      <Toolbar>
        {/* Every one of these is secondary — this screen has no search box and
            nothing here is the reason a person opened it. At 390px the five of
            them pushed the events list past y=885, so the log showed no log.
            One press on a phone, inline on a desktop; see OfficePage's
            MoreFilters for the three rules it follows. */}
        <MoreFilters active={countActive(
          filters.entity_type, filters.user_id, filters.action_type, filters.start_date, filters.end_date,
        )}
        >
        {/*
          Both type lists come from /activity/stats, so ONE refused request
          empties two pickers. An empty select is a claim — "there is no such
          record type" — and a refusal makes it a false one that looks like
          data rather than like an error, which is why it gets a sentence and
          stays usable rather than being greyed out.
        */}
        <FwField
          label="Record type"
          htmlFor="or-activity-type"
          error={pickerNote(statsQ.error, 'record type')}
          hint={statsQ.error ? undefined : `Types seen in the ${periodText}`}
        >
          <FwSelect
            value={filters.entity_type}
            onChange={(e) => setFilter('entity_type', e.target.value)}
            disabled={!!entityTypes && entityTypes.length === 0}
          >
            <option value="">All types</option>
            {(entityTypes || []).map((t) => (
              <option key={t.entity_type} value={t.entity_type}>{labelOf(t.entity_type)}</option>
            ))}
          </FwSelect>
        </FwField>

        {/*
          THE ROW THIS SCREEN EXISTS FOR. Inactive people are kept in the list
          rather than filtered out the way Reports does: a departed employee's
          actions are the single most likely thing anyone opens an audit log to
          find, so dropping them would make the one question the filter is for
          unanswerable. The label says which are inactive so nobody reads a
          leaver as current staff.
        */}
        <FwField label="Person" htmlFor="or-activity-user" error={pickerNote(usersQ.error, 'person')}>
          <FwSelect
            value={filters.user_id}
            onChange={(e) => setFilter('user_id', e.target.value)}
            disabled={!!users && users.length === 0}
          >
            <option value="">Everyone</option>
            {(users || []).map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.is_active === 0 || u.is_active === false ? `${u.name} (inactive)` : u.name}
              </option>
            ))}
          </FwSelect>
        </FwField>

        <FwField
          label="Action"
          htmlFor="or-activity-action"
          error={pickerNote(statsQ.error, 'action')}
          hint={statsQ.error ? undefined : `Actions seen in the ${periodText}`}
        >
          <FwSelect
            value={filters.action_type}
            onChange={(e) => setFilter('action_type', e.target.value)}
            disabled={!!actionTypes && actionTypes.length === 0}
          >
            <option value="">Any action</option>
            {(actionTypes || []).map((a) => (
              <option key={a.action_type} value={a.action_type}>{labelOf(a.action_type)}</option>
            ))}
          </FwSelect>
        </FwField>

        <FwField label="From" htmlFor="or-activity-from">
          <FwInput type="date" value={filters.start_date} onChange={(e) => setFilter('start_date', e.target.value)} />
        </FwField>
        <FwField
          label="To"
          htmlFor="or-activity-to"
          error={rangeOk ? null : 'The end date is before the start date, so nothing can match.'}
        >
          <FwInput type="date" value={filters.end_date} onChange={(e) => setFilter('end_date', e.target.value)} />
        </FwField>

        <FwButton variant="ghost" onClick={clearFilters} disabled={!filtered}>Clear filters</FwButton>
        </MoreFilters>
      </Toolbar>

      <Panel title="Events">
        {listQ.isPending && rangeOk ? <Skeleton lines={6} /> : null}
        {!rangeOk ? <p className="or-muted">Pick an end date on or after the start date.</p> : null}
        {listQ.error ? (
          <Notice tone="stop" data-panel-error="activity-log">
            {serverSentence(listQ.error, 'Could not load the activity log. This is not an empty log — try again.')}
          </Notice>
        ) : null}
        {/*
          A 200 whose body has no `activity` array is a failure wearing a
          success. It must not fall through to the empty state, which would
          report it as "nothing has been logged yet".
        */}
        {!listQ.isPending && !listQ.error && rangeOk && !rows ? (
          <Notice tone="stop" data-panel-error="activity-log">
            The server&apos;s answer could not be read, so no events are shown. This is not an empty log.
          </Notice>
        ) : null}

        {rows && rows.length === 0 ? (
          <EmptyState
            icon="activity"
            title={filtered ? 'Nothing matched these filters' : 'Nothing has been logged yet'}
            data-empty="activity"
          >
            {filtered
              ? 'Clear a filter, widen the dates, or pick a different person.'
              : 'Events appear here as the crew and the office work.'}
          </EmptyState>
        ) : null}

        {rows && rows.length > 0 ? (
          <>
            <ul className="or-feed">
              {rows.map((a) => (
                <li key={a.id} className="or-feed__item" data-activity={a.id}>
                  <div className="or-feed__text">{a.description || labelOf(a.action_type)}</div>
                  <div className="or-feed__meta">
                    {a.user_name ? (
                      <>
                        <Pill tone={ROLE_TONE[a.user_role] || 'neutral'}>{a.user_name}</Pill>{' '}
                      </>
                    ) : null}
                    {[...metaBits(a), fmtDateTime(a.created_at)].filter((s) => s && s !== DASH).join(' · ')}
                  </div>
                </li>
              ))}
            </ul>
            {range ? <p className="or-muted" data-pager="activity">Showing {range}</p> : null}
            <div className="or-actions">
              <FwButton
                variant="ghost"
                icon="chevronLeft"
                onClick={() => setOffset(Math.max(0, Number(body.offset) - (Number(body.limit) || PAGE_SIZE)))}
                disabled={!prevPage}
              >
                Previous
              </FwButton>
              <FwButton
                variant="ghost"
                icon="chevronRight"
                onClick={() => setOffset(Number(body.offset) + rows.length)}
                disabled={!nextPage}
              >
                Next
              </FwButton>
            </div>
          </>
        ) : null}
      </Panel>
    </div>
  );
}
